import React, { useState } from "react";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { createUseStyles } from "react-jss";
import { colors } from "../../styles/colors";
import { CustomTypography } from "../CustomTypography/CustomTypography";
import { Icon } from "../Icon/Icon";
import { Z } from "../../styles/zIndex";

const useStyles = createUseStyles({
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: Z.MODAL,
  },
  modal: {
    backgroundColor: colors.surface,
    borderRadius: "16px",
    padding: "32px",
    maxWidth: "420px",
    width: "90%",
    textAlign: "center" as const,
    border: `1px solid ${colors.borderLight}`,
  },
  iconWrap: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    backgroundColor: "#FFF3E0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
  },
  title: { marginBottom: "8px" },
  desc: {
    fontSize: "14px",
    color: colors.textSecondary || "#888",
    lineHeight: "1.5",
    marginBottom: "24px",
  },
  btnRow: { display: "flex", gap: "12px", justifyContent: "center" },
  btnUpgrade: {
    padding: "10px 24px",
    borderRadius: "8px",
    backgroundColor: colors.primary,
    color: "#fff",
    border: "none",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "14px",
  },
  btnClose: {
    padding: "10px 24px",
    borderRadius: "8px",
    backgroundColor: "transparent",
    color: colors.textSecondary || "#888",
    border: `1px solid ${colors.borderLight}`,
    cursor: "pointer",
    fontWeight: 500,
    fontSize: "14px",
  },
  lockedOverlay: { position: "relative" as const },
  lockBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "11px",
    fontWeight: 500,
    color: "#E65100",
    backgroundColor: "#FFF3E0",
    padding: "2px 8px",
    borderRadius: "10px",
  },
});

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  /** Null when the server has not (yet) told us which offer sells it. */
  requiredPlan: string | null;
  featureName?: string;
}

export const UpgradeModal: React.FC<UpgradeModalProps> = ({ open, onClose, requiredPlan, featureName }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (!open) return null;
  return (
    <div className={classes.overlay} onClick={onClose}>
      <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
        <div className={classes.iconWrap}>
          <Icon name="lock" width="28px" height="28px" color={"#E65100"} />
        </div>
        <div className={classes.title}>
          <CustomTypography text={t("billing.upgradeRequired", "Upgrade Required")} variant="h4" />
        </div>
        <p className={classes.desc}>
          {requiredPlan
            ? featureName
              ? t("billing.featureRequiresPlan", "{{feature}} requires the {{plan}} plan or higher.", { feature: featureName, plan: requiredPlan })
              : t("billing.upgradeToAccess", "This feature requires the {{plan}} plan.", { plan: requiredPlan })
            : featureName
              ? t("billing.featureNotIncluded", "{{feature}} is not included in your current plan.", { feature: featureName })
              : t("billing.notIncluded", "This feature is not included in your current plan.")}
        </p>
        <div className={classes.btnRow}>
          <button className={classes.btnClose} onClick={onClose}>{t("common.cancel", "Cancel")}</button>
          <button className={classes.btnUpgrade} onClick={() => { onClose(); navigate("/billing"); }}>{t("billing.viewPlans", "View Plans")}</button>
        </div>
      </div>
    </div>
  );
};

interface FeatureGateProps {
  feature: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  mode?: "hide" | "lock" | "blur";
}

export const FeatureGate: React.FC<FeatureGateProps> = ({ feature, children, fallback, mode = "lock" }) => {
  const { hasFeature, requiresUpgrade, featureLabel, isLoading } = useSubscription();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const classes = useStyles();

  if (isLoading) return <>{children}</>;
  if (hasFeature(feature)) return <>{children}</>;

  // Never fall back to a hardcoded plan name. If the server has not told us
  // which offer sells this feature, `requiredPlan` stays null and the modal
  // shows a generic prompt - naming a tier that may have been renamed or
  // retired would send the customer looking for something that does not exist.
  const requiredPlan = requiresUpgrade(feature);

  if (mode === "hide") return <>{fallback ?? null}</>;

  return (
    <>
      <div
        className={classes.lockedOverlay}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowUpgrade(true); }}
        style={{ cursor: "pointer", filter: mode === "blur" ? "blur(3px)" : undefined, pointerEvents: "all" }}
      >
        {children}
      </div>
      <UpgradeModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        requiredPlan={requiredPlan}
        // Label comes from the server catalogue; the underscore-stripped key
        // ("eco driving") is only a fallback for a feature we have no label for.
        featureName={featureLabel(feature)}
      />
    </>
  );
};

export const LockBadge: React.FC<{ plan?: string }> = ({ plan }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  return (
    <span className={classes.lockBadge}>
      <Icon name="lock" width="12px" height="12px" color={"#E65100"} />
      {plan ?? t("billing.upgrade", "Upgrade")}
    </span>
  );
};
